import React, { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { formatNumber, cn } from "../../lib/utils";

// Shared bits for class-change lists (Rule What-If preview and the Morning Brief alerts).
export const cls = (c) => (c === "ZI" ? "Z-Int" : c);

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function ClassMove({ from, to, testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 font-mono-tabular text-[11px] whitespace-nowrap">
      <span className="px-1.5 py-0.5 border border-border rounded-sm text-muted">{cls(from)}</span>
      <ArrowRight size={10} className="text-primary" />
      <span className="px-1.5 py-0.5 border border-primary/50 bg-primary/10 text-primary rounded-sm font-semibold">{cls(to)}</span>
    </span>
  );
}

export function TransitionChips({ transitions, active, onPick, testid }) {
  if (!transitions?.length) return null;
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {transitions.slice(0, 14).map(t => {
        const key = `${t.from}>${t.to}`;
        const on = active === key;
        return (
          <button key={key} type="button" data-testid={`${testid}-${t.from}-${t.to}`} onClick={() => onPick(on ? null : key)}
                  className={cn("inline-flex items-center gap-1.5 px-2 py-1 border rounded-sm text-[10.5px] transition-colors",
                    on ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/60")}>
            <span className="font-mono-tabular font-semibold">{cls(t.from)} → {cls(t.to)}</span>
            <span className="text-muted font-mono-tabular">{formatNumber(t.count)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SearchBox({ value, onChange, testid, placeholder = "Search SKU code or name" }) {
  return (
    <div className="relative flex-1 min-w-[180px] max-w-[340px]">
      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
      <input data-testid={testid} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
             className="w-full bg-bg border border-border pl-7 pr-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-primary transition-colors" />
    </div>
  );
}

export function Pager({ data, onPage, testid }) {
  if (!data || !data.total) return null;
  const from = (data.page - 1) * data.page_size + 1;
  const to = Math.min(data.total, data.page * data.page_size);
  const btn = "w-7 h-7 border border-border rounded-sm inline-flex items-center justify-center hover:border-primary disabled:opacity-30";
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-border text-[11px] text-muted">
      <span className="font-mono-tabular" data-testid={`${testid}-range`}>Rows {formatNumber(from)}–{formatNumber(to)} of {formatNumber(data.total)}</span>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`${testid}-prev`} disabled={data.page <= 1} onClick={() => onPage(data.page - 1)} className={btn}><ChevronLeft size={12} /></button>
        <span className="font-mono-tabular" data-testid={`${testid}-page`}>Page {data.page} / {data.pages}</span>
        <button type="button" data-testid={`${testid}-next`} disabled={data.page >= data.pages} onClick={() => onPage(data.page + 1)} className={btn}><ChevronRight size={12} /></button>
      </div>
    </div>
  );
}
